import { defineWidget } from '../../client/lib/contract/helpers'
import { t } from '../../client/i18n'
import type { BarDatum, ConfigField, HostProxyHealth, HostServiceProbe, WidgetRenderMeta, WidgetRenderOut, WidgetStats } from '../../client/lib/contract/types'

/**
 * 本地依赖 (sys-services) — this machine's LOCAL DEPENDENCIES: the endpoints the
 * work hangs off, and — the one reading nothing else can give you — whether
 * traffic can actually LEAVE this machine. 2×2.
 *
 *   ┌ 本地依赖 ────────────────────┐
 *   │ 出口正常                      │   ← headAfter.big: the egress VERDICT, as a word
 *   │ 出口 1.3s                     │   ← legend: the reading behind that verdict
 *   │ ──────────────────────────────│   hairline (breakdown's own)
 *   │ DSH web           通 · 1ms    │
 *   │ Ollama            通 · 2ms    │   ← opt-in row (config), OFF by default
 *   └───────────────────────────────┘
 *
 * ── WHAT 本地依赖 MEANS, AND WHO IT IS FOR (the owner asked — see README §0) ──
 * The card used to be called 本地服务, which named no subject: a reader could not
 * tell whether it listed processes, model runtimes, or anything else that
 * listens. It is neither a process table (that is 进程内存占用 / sys-procs) nor a
 * general service monitor. It answers ONE question for the person running long
 * agent sessions on THIS machine — "when everything got slow, WHICH dependency
 * broke?" — with two subjects:
 *  - the local endpoints worth watching (the DSH web port, plus the two local
 *    model runtimes when the user says they run them), and
 *  - the proxy EGRESS, the part nothing else can show: git / gh / every web fetch
 *    on this machine goes through 127.0.0.1:10808, so a dead proxy presents as
 *    "everything is slow" and is invisible in every other card.
 * Deliberately NOT covered: MCP server health, CUDA / drivers (the GPU cards),
 * bandwidth (网络吞吐 / sys-net). See README §0 for the full boundary.
 *
 * ── WHY THE TWO MODEL RUNTIMES ARE OPT-IN (default OFF) ─────────────────────
 * The host probes four FIXED ports (src/host/overview.ts / SERVICE_TARGETS). A
 * machine that does not run LM Studio therefore showed a permanently red 未运行
 * row — a FALSE ALARM the card generated by itself, which is exactly what the
 * owner objected to. An endpoint the user does not have is not a fault; it is not
 * this card's subject. So Ollama / LM Studio get a row ONLY when their toggle is
 * on, and a switched-off port prints NOTHING — not a greyed row, not a `—`.
 * (Making the port list itself configurable is a v1 boundary: see README §10.)
 *
 * ── WHY THE BIG FIGURE IS A WORD, NOT `N / M` ───────────────────────────────
 * With the model runtimes off, the default tile watches one endpoint plus the
 * egress, so "1 / 2" counts nothing a reader cares about. The figure is the
 * EGRESS VERDICT instead — 出口正常 / 出口不通 / 检测中 — a word that needs no
 * explanation and is this card's one irreplaceable reading. The caption under it
 * carries the EVIDENCE (the millisecond reading, the failure code, or the proxy
 * port's own handshake), never the verdict again: one fact, one place.
 *
 * ── TONE DIRECTION (this widget owns it; the renderer never guesses) ─────────
 * The headline makes ONE claim — the egress verdict — so only that verdict
 * colours it: 出口不通 → `danger`, 检测中 → `muted` ("I do not know yet" is NOT a
 * fault), 出口正常 → no tone. A local endpoint that is down paints its OWN row
 * `danger`; it must not redden a headline that is talking about the proxy (the
 * previous build folded the endpoint count into the headline, which turned an
 * uninstalled LM Studio into a card-wide red alarm).
 *
 * ── WHY `tcpMs` IS NEVER THE PROXY VERDICT ──────────────────────────────────
 * Measured 2026-09-29 on this machine's xray: a CONNECT to the proxy returns
 * `200 Connection established` even for a domain that does NOT exist. So a fast
 * `tcpMs` proves only that the port is listening — exactly the confusion this
 * card was built to end. The honest verdict is `ok`, produced host-side by an
 * ABSOLUTE-URI GET (`GET http://host/path HTTP/1.1` — what actually makes the
 * proxy resolve a target and carry bytes), and `ok === null` means that probe is
 * still in flight (stale-while-revalidate). `null` renders 检测中, NEVER a fault:
 * printing a fault for "still measuring" would be a lie the owner would act on.
 *
 * ── WHY THE PROXY IS NOT A BODY ROW ─────────────────────────────────────────
 * The proxy's port AND its egress verdict are the head, so it must not appear a
 * second time as a body row — one fact, one place (the same discipline that keeps
 * sys-procs from repeating its #1 process in the detail rows).
 *
 * The render is pure and reads no clock; every string is produced here.
 */

/** Body rows the tile carries. The head is three rungs + a caption; a fourth row
 *  pushes past the 126px content box (see WORKER-BRIEF §2 height budget). */
const MAX_ROWS = 3

/** Sub-millisecond handshakes must not round to a flat `0ms` (they are the normal
 *  case on loopback, and `0ms` reads as "no measurement"). */
const MS_FLOOR_DECIMALS = 10

/**
 * The endpoints that are OPT-IN, each with the config key (a 组件配置 toggle) that
 * reveals it. Everything else the host probes is a CORE dependency and always
 * gets a row: the host's list is the fixed, curated one (SERVICE_TARGETS), so a
 * probe added there is by definition worth showing.
 */
const OPTIONAL_ENDPOINTS: Array<{ key: string; flag: string }> = [
  { key: 'ollama', flag: 'showOllama' },
  { key: 'lmstudio', flag: 'showLmStudio' },
]
const OPTIONAL_KEYS = new Set(OPTIONAL_ENDPOINTS.map((o) => o.key))

/**
 * A millisecond reading as a SHORT label — and this is why the unit is decided
 * here rather than by the caller: the two quantities this card prints live on
 * opposite ends of the scale. A loopback handshake is 0.2–2 ms, while the egress
 * GET through the proxy is ~1.3 s (measured 2026-09-29), and `1311ms` in a
 * grey caption is four glyphs of noise.
 *
 *  - `0.2ms` / `2ms` — sub-millisecond keeps one decimal (never a flat `0ms`,
 *    which reads as "no measurement" for the most common reading on this machine);
 *  - `1.3s`          — a second or more switches unit, the same shape
 *    `fmtDuration` uses for tool calls;
 *  - `null`          — no reading at all (the probe failed, or the field has not
 *    answered yet); the CALLER picks the word, because an unknown DURATION is not
 *    an unknown STATE.
 */
function fmtMs(ms: number | null): string | null {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return null
  if (ms >= 1000) return `${Math.round(ms / 100) / 10}s`
  if (ms < 1) return `${Math.max(1, Math.round(ms * MS_FLOOR_DECIMALS)) / MS_FLOOR_DECIMALS}ms`
  return `${Math.round(ms)}ms`
}

/** The three things the proxy can be telling us — `ok` alone is not enough,
 *  because `null` is a THIRD state (probe in flight), not a failure. */
type ProxyState = 'ok' | 'fail' | 'pending'

/**
 * How long a failure code may be before the caption stops carrying it.
 *
 * MEASURED IN THE REAL CARD (chrome, the gallery page's own tokens and font,
 * 2026-09-30), not carried over from the previous build's estimate. The caption
 * is one nowrap ellipsized line (`.dsx-stats-card-legend`) whose content box
 * measures 124px in a 150px tile at the 10px caption size — that part still
 * holds, but the CODE budget does not: the earlier build wrapped the code as
 * 出口不通（CODE）, which spent 40px of the line, so only nine characters fit and
 * `ECONNRESET` clipped. The verdict now lives in the big figure and the caption
 * is the code ALONE, and the same measurement gives:
 *
 *   ECONNREFUSED     (12)  78.9px   fits       CONNECTION_RESET (16) 100.6px  fits
 *   proxy 0.2ms      (11)  50.0px   fits       124px is the whole budget
 *
 * Sixteen characters is therefore the cap — the longest error code this host can
 * produce (`CONNECTION_RESET`, `ERR_SOCKET_CLOSED`) rides the caption with ~23px
 * of slack, and `ERROR_CODE_CHARS = 12` would have thrown that diagnosis away for
 * no reason. It is a CHARACTER count rather than a pixel width because a widget
 * render is pure data — it has no font metrics. Anything longer falls back to the
 * proxy port's own handshake instead of printing a truncated code, and the FULL
 * text stays one hover away (`cardHint`).
 */
const ERROR_CODE_CHARS = 16

/** Leading words that are noise when the failure is named: `connect ECONNREFUSED
 *  …` names its error in the SECOND token, and keeping the verb would print the
 *  least informative half of the diagnosis. */
const ERROR_NOISE = new Set(['connect', 'connect:', 'get', 'request', 'socket', 'error:'])

/**
 * A failure code as a SHORT caption token.
 *
 * Probe errors arrive in whatever shape the underlying error carries:
 * `ECONNRESET`, `timeout`, `port-closed`, or `connect ECONNREFUSED
 * 127.0.0.1:10808`. The target address half is long, redundant with the port
 * already on the tile, and the part that pushes the caption past its budget — so
 * the code is reduced to its NAMING token: the first token that is not a leading
 * verb (see ERROR_NOISE). A code with no separator is left whole, and the length
 * cap then decides whether it fits.
 */
function shortErrorCode(raw: string): string {
  const tokens = raw.trim().split(/[\s,;]+/).filter((tok) => tok !== '')
  for (const tok of tokens) {
    if (!ERROR_NOISE.has(tok.toLowerCase())) return tok
  }
  return tokens[0] ?? ''
}

/** A code short enough to ride the caption, or null when the caption must show
 *  the port's handshake instead. */
function captionCode(raw: string): string | null {
  if (raw === '') return null
  const code = shortErrorCode(raw)
  return code.length <= ERROR_CODE_CHARS ? code : null
}

/** The proxy head, normalised once so the render body reads no conditionals. */
interface ProxyOut {
  state: ProxyState
  /** The BIG figure: the verdict as a word (never a count — see the header). */
  verdict: string
  /** The grey caption: the EVIDENCE for that verdict, or null when there is none
   *  to print (an absent caption is honest; a repeated verdict is noise). */
  legend: string | null
  /** Hover text: the rule, and the specific failure when there is one. */
  hint: string
}

/**
 * The proxy PORT's own handshake as the caption — `代理 0.2ms`.
 *
 * Used whenever the caption needs a reading rather than a verdict: while the
 * egress probe is still in flight (this handshake is the only measurement that
 * has come back), and when the egress failed without a code short enough to
 * print. It is deliberately NOT printed beside a completed verdict: `egressMs` is
 * then an end-to-end measurement THROUGH the proxy, and the handshake would spend
 * the one caption line restating a number the verdict already dominates.
 */
function portLegend(proxy: HostProxyHealth | null): string | null {
  const tcp = fmtMs(proxy?.tcpMs ?? null)
  return tcp === null ? null : t('card.sys-services.egressPort', { ms: tcp })
}

/**
 * Turn the host's proxy section into verdict + caption + hover.
 *
 * Order matters: `ok === true` wins even when a later field is missing, because a
 * completed egress measurement IS the verdict; `null` is checked BEFORE any falsy
 * test so an in-flight probe can never be rendered as a fault.
 *
 * The caption never repeats the verdict word. `出口不通` is the big figure, so the
 * caption carries the REASON (the code) or, failing that, the port's handshake —
 * a `null` caption is a real shape (nothing measurable to add) and prints no line
 * rather than inventing a placeholder.
 */
function proxyOut(proxy: HostProxyHealth | null): ProxyOut {
  const rule = t('card.sys-services.noRule')
  if (proxy === null || proxy.ok === null) {
    return { state: 'pending', verdict: t('card.sys-services.verdictWait'), legend: portLegend(proxy), hint: rule }
  }
  if (proxy.ok) {
    const ms = fmtMs(proxy.egressMs)
    return {
      state: 'ok',
      verdict: t('card.sys-services.verdictOk'),
      legend: ms === null ? null : t('card.sys-services.egressOk', { ms }),
      hint: rule,
    }
  }
  const raw = typeof proxy.error === 'string' ? proxy.error.trim() : ''
  return {
    state: 'fail',
    verdict: t('card.sys-services.verdictFail'),
    legend: captionCode(raw) ?? portLegend(proxy),
    hint: raw === '' ? rule : `${t('card.sys-services.egressDetail', { error: raw })} · ${rule}`,
  }
}

/** One body row: the endpoint's label plus its state (and latency when it is up). */
function serviceRow(svc: HostServiceProbe): { label: string; value: string; tone?: BarDatum['tone'] } {
  if (!svc.up) return { label: svc.label, value: t('card.sys-services.down'), tone: 'danger' as const }
  const ms = fmtMs(svc.ms)
  return {
    label: svc.label,
    value: ms === null ? t('card.sys-services.up') : `${t('card.sys-services.up')} · ${ms}`,
  }
}

/**
 * Is an opt-in endpoint switched on?
 *
 * The per-instance config rides ON `stats` (the shell merges `cardConfigs[key]`
 * last — see runtime/live-stats.ts), so a card nobody has configured and a card
 * whose switch is off are the SAME read: only an explicit `true` reveals a row.
 * `meta.sim` may pin the same flag for the offline preview, which is how the
 * gallery shows the opt-in shapes without a config editor.
 */
function flagOn(stats: WidgetStats, sim: Record<string, unknown> | undefined, flag: string): boolean {
  return (sim?.[flag] ?? stats[flag]) === true
}

/**
 * The endpoint rows this instance shows.
 *
 * Core endpoints always; the two model runtimes only when their switch is on.
 * A switched-off endpoint produces NO row at all — the alternative (a greyed row
 * or a `—`) would keep the false alarm on the tile, which is the whole defect
 * this filtering removes. The proxy is always dropped here: its state IS the head.
 */
function endpointRows(services: HostServiceProbe[], stats: WidgetStats, sim: Record<string, unknown> | undefined): HostServiceProbe[] {
  const on = new Set(OPTIONAL_ENDPOINTS.filter((o) => flagOn(stats, sim, o.flag)).map((o) => o.key))
  return services.filter((svc) => svc.key !== 'proxy' && (!OPTIONAL_KEYS.has(svc.key) || on.has(svc.key)))
}

function sysServicesRender(stats: WidgetStats, meta?: WidgetRenderMeta): WidgetRenderOut | null {
  const services = stats.host?.services
  // No probe at all = no subject. An EMPTY array is also nothing to report (this
  // card's whole content is the endpoints), and a null `host` means the route has
  // not answered — the shell owns the loading skeleton, not this render.
  if (!Array.isArray(services) || services.length === 0) return null

  const sim = meta?.sim
  // The preview override: `meta.sim` is an UNTYPED bag, so it is trusted only
  // when it really is an object (or an explicit null) and only for the proxy
  // section — it lets the offline gallery / market preview walk
  // ok → fail → pending without waiting for a real egress failure or a 1.3s
  // in-flight window (see `example.simSteps`). The endpoint LIST is never
  // overridden: the sim steps carry no `services`, and the live/exampled record
  // stays the single source for it (the sim only pins which OPT-IN endpoints show).
  const simProxy = sim?.proxy
  const proxy = typeof simProxy === 'object'
    ? (simProxy as HostProxyHealth | null) ?? stats.host?.proxy ?? null
    : stats.host?.proxy ?? null
  const p = proxyOut(proxy)

  const rows = endpointRows(services, stats, sim).slice(0, MAX_ROWS).map(serviceRow)

  return {
    title: t('card.sys-services.title'),
    // The head's ladder, one rung per field: the verdict word here, its evidence
    // in the caption below. `value` is deliberately NOT set — with a `headAfter`
    // head it would print the same figure a second time in the body.
    headAfter: { big: p.verdict },
    ...(p.legend === null ? {} : { legend: p.legend }),
    // The headline speaks ONLY for the egress (see the tone note in the header):
    // its own failure is danger, an in-flight probe is muted, and a good verdict
    // stays plain. A down ENDPOINT colours its own row and nothing else.
    ...(p.state === 'fail' ? { valueTone: 'danger' as const } : p.state === 'pending' ? { valueTone: 'muted' as const } : {}),
    cardHint: p.hint,
    // The rows keep the tile's floor, the same posture as 缓存命中 / 工具调用: a
    // `headAfter` head alone would leave the slack UNDER the rows.
    bodyAnchor: 'bottom',
    ...(rows.length > 0 ? { chart: { kind: 'breakdown' as const, breakdown: rows } } : {}),
  }
}

/** The proxy section mirroring this machine (2026-09-29): the xray port answers
 *  a TCP handshake in ~0.2 ms and one absolute-URI GET takes ~1.3 s. */
const SIM_OK: HostProxyHealth = { host: '127.0.0.1', port: 10808, tcpMs: 0.2, egressMs: 1311, ok: true, error: null }
/** A dead egress: the port is listening (tcpMs is real) but no traffic leaves.
 *  The error is the FULL shape a probe error really carries (a name plus its
 *  target address) — the caption keeps only the naming token and the whole string
 *  rides the hover, which is exactly the path this preview must exercise. */
const SIM_FAIL: HostProxyHealth = { host: '127.0.0.1', port: 10808, tcpMs: 0.3, egressMs: null, ok: false, error: 'ECONNREFUSED 127.0.0.1:10808' }
/** The same failure with the LONGEST code the probe can produce (16 characters).
 *  It is a preview state on purpose: this is the one case where the caption's
 *  character budget is load-bearing, so it has to be eyeballed on a real card
 *  (`CONNECTION_RESET` is also the code the PREVIOUS build had to drop). */
const SIM_FAIL_LONG: HostProxyHealth = { host: '127.0.0.1', port: 10808, tcpMs: 0.3, egressMs: null, ok: false, error: 'CONNECTION_RESET 127.0.0.1:10808' }
/** In flight (stale-while-revalidate): `ok` is null, i.e. NOT a verdict. */
const SIM_PENDING: HostProxyHealth = { host: '127.0.0.1', port: 10808, tcpMs: 0.2, egressMs: null, ok: null, error: null }

/**
 * The preview states a click walks through (`example.simSteps`), one cell each in
 * the gallery — they cover the six shapes this card has to be reviewed in:
 *
 *   1. the DEFAULT tile: the egress is fine and only the core endpoint (DSH web)
 *      has a row — no LM Studio row, therefore no false alarm;
 *   2. Ollama switched on (the opt-in shape a user who runs a local model gets);
 *   3. both model runtimes on, LM Studio DOWN — the "a monitored service is down"
 *      shape, and the one the old build showed by accident, now deliberate;
 *   4. egress down, with its failure code where the caption can carry it;
 *   5. egress down with the LONGEST possible code — the caption-budget case;
 *   6. the probe still in flight — 检测中, never a fault.
 *
 * `simSteps[0]` MUST be byte-identical to the example's own `sim`: the shell
 * locates the CURRENT step by deep comparison, so a mismatch makes the first
 * click a silent no-op.
 */
const SIM_STEPS: Array<Record<string, unknown>> = [
  { proxy: SIM_OK },
  { proxy: SIM_OK, showOllama: true },
  { proxy: SIM_OK, showOllama: true, showLmStudio: true },
  { proxy: SIM_FAIL, showOllama: true },
  { proxy: SIM_FAIL_LONG },
  { proxy: SIM_PENDING },
]

/**
 * Preview data — THIS machine's real readings (measured 2026-09-29): DSH web up at
 * 1 ms, Ollama up at 2 ms, LM Studio NOT running, the proxy port up at 0.2 ms with
 * a 1311 ms egress. The endpoint list carries all four probes so that switching an
 * opt-in toggle on in 组件配置 immediately has a row to reveal; `ok: true` is what
 * makes the first gallery cell the ordinary state instead of an artificial failure.
 */
const EXAMPLE_STATS: Partial<WidgetStats> = {
  host: {
    ts: 0,
    net: null,
    power: null,
    procs: null,
    services: [
      { key: 'dsh', label: 'DSH web', host: '127.0.0.1', port: 3080, up: true, ms: 1 },
      { key: 'ollama', label: 'Ollama', host: '127.0.0.1', port: 11434, up: true, ms: 2 },
      { key: 'lmstudio', label: 'LM Studio', host: '127.0.0.1', port: 1234, up: false, ms: null },
      { key: 'proxy', label: 'Proxy', host: '127.0.0.1', port: 10808, up: true, ms: 0.2 },
    ],
    proxy: SIM_OK,
  },
}

export default defineWidget({
  id: 'sys-services',
  name: () => t('widget.sys-services.name'),
  desc: () => t('widget.sys-services.desc'),
  builtin: true,
  group: 'device',
  // MUST mirror the manifest: the runtime's sizesOf() reads THIS descriptor.
  sizes: ['2x2'],
  render: sysServicesRender,
  simToggle: () => t('card.sys-services.simToggle'),
  // The two model runtimes are the ONLY configurable part of this card: the
  // endpoint list itself is the host's fixed set, so these switches choose which
  // of those endpoints are this card's business (see the header).
  configSchema: [
    {
      key: 'showOllama',
      label: () => t('card.sys-services.cfg.ollama'),
      type: 'toggle',
      default: false,
      hint: () => t('card.sys-services.cfg.hint'),
    } satisfies ConfigField,
    {
      key: 'showLmStudio',
      label: () => t('card.sys-services.cfg.lmstudio'),
      type: 'toggle',
      default: false,
    } satisfies ConfigField,
  ],
  example: {
    stats: EXAMPLE_STATS,
    sim: SIM_STEPS[0],
    simSteps: SIM_STEPS,
  },
})
